import type { StorefrontAdapter, StorefrontCartItem } from '../types';
import { loadScriptOnce } from '../types';

declare global {
  interface Window {
    omnisend?: unknown[] & { identifyContact?: (c: Record<string, unknown>) => void };
  }
}

/** Omnisend's queue: `omnisend.push([...])` buffers until launcher-v2.js drains it. */
function queue(): any {
  if (typeof window === 'undefined') return null;
  if (!window.omnisend) window.omnisend = [] as any;
  return window.omnisend;
}

/** "viewed product" v4 and the cart events take prices in CENTS. */
function cents(n: unknown): number {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? Math.round(v * 100) : 0;
}

function eventId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

let liveConfig: Record<string, string> | null = null;

function lineItems(cart: StorefrontCartItem[], currency: string) {
  return cart.map((i) => ({
    productID: i.product_id,
    productVariantID: i.variant_id,
    productTitle: i.product_name,
    productVariantTitle: i.variant_name ?? undefined,
    productSKU: i.sku ?? undefined,
    productPrice: cents(i.price),
    productQuantity: i.quantity,
    productImageURL: i.image_url ?? undefined,
    currency,
  }));
}

function track(name: string, version: string, properties: Record<string, unknown>): void {
  const q = queue();
  if (!q) return;
  q.push(['track', name, { origin: 'api', eventID: eventId(), eventVersion: version, eventTime: new Date().toISOString(), properties }]);
}

export const omnisendAdapter: StorefrontAdapter = {
  id: 'omnisend',
  isConfigured: (cfg) => typeof cfg.brand_id === 'string' && /^[A-Za-z0-9_-]{6,64}$/.test(cfg.brand_id),
  load(cfg) {
    liveConfig = cfg;
    const q = queue();
    if (!q) return;
    q.push(['brandID', cfg.brand_id]);
    q.push(['track', '$pageViewed']);
    loadScriptOnce('omnisend-launcher', 'https://omnisnippet1.com/inshop/launcher-v2.js');
  },
  identify(user) {
    const q = queue();
    if (!q || !user.email) return;
    // Required for product/cart events to count: Omnisend only records them for identified contacts.
    if (typeof q.identifyContact === 'function') q.identifyContact({ email: user.email });
    else q.push(['identifyContact', { email: user.email }]);
  },
  viewedProduct(p) {
    const currency = liveConfig?.currency || 'USD';
    track('viewed product', 'v4', {
      product: {
        id: p.id,
        title: p.name,
        price: cents(p.price),
        oldPrice: p.compare_at_price != null ? cents(p.compare_at_price) : undefined,
        currency,
        imageUrl: p.image_url ?? undefined,
        url: p.url ?? (typeof window !== 'undefined' ? window.location.href : undefined),
        status: 'inStock',
      },
    });
  },
  addedToCart(item, cart) {
    const currency = liveConfig?.currency || 'USD';
    track('added product to cart', '', {
      currency,
      value: cents(cart.reduce((s, i) => s + i.price * i.quantity, 0)),
      abandonedCheckoutURL: typeof window !== 'undefined' ? `${window.location.origin}/checkout` : undefined,
      addedItem: lineItems([item], currency)[0],
      lineItems: lineItems(cart, currency),
    });
  },
  startedCheckout(cart, total) {
    if (cart.length === 0) return;
    const currency = liveConfig?.currency || 'USD';
    track('started checkout', '', {
      currency,
      value: cents(total),
      abandonedCheckoutURL: typeof window !== 'undefined' ? `${window.location.origin}/checkout` : undefined,
      lineItems: lineItems(cart, currency),
    });
  },
  // "placed order" / "paid for order" are sent server-side with the order id; nothing client-side.
  orderCompleted() {},
  refresh() {
    const q = queue();
    if (q && liveConfig) q.push(['track', '$pageViewed']);
  },
};

/** Test seam. */
export function _resetOmnisendAdapterForTests(): void {
  liveConfig = null;
}
